import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import {
  Mail,
  Check,
  X as XIcon,
  MessageSquare,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  interviewInvitationsApi,
  InterviewInvitationWithEmployer,
  InterviewInvitationStatus,
} from '../services/interviewInvitationsApi';
import './Pasiulymai.scss';

/**
 * "Pasiūlymai" — employee-facing list of incoming interview invitations.
 *
 * Visual language matches FreelanceGigsList for cross-feature parity:
 * page-title with brand icon, pill status tabs, white cards with the
 * teal-on-hover affordance, red round unread pills.
 */

type TabKey = 'all' | 'pending' | 'accepted' | 'archived';

const STATUS_LABEL: Record<InterviewInvitationStatus, string> = {
  pending: 'Laukia atsakymo',
  accepted: 'Aktyvus',
  declined: 'Atmesta',
  withdrawn: 'Atšaukta',
};

function formatDateTime(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('lt-LT');
}

function employerDisplay(row: InterviewInvitationWithEmployer): string {
  if (row.employer_company_name && row.employer_company_name.trim()) {
    return row.employer_company_name.trim();
  }
  const personal = `${row.employer_first_name ?? ''} ${row.employer_last_name ?? ''}`.trim();
  if (personal) return personal;
  return row.employer_email ?? 'Darbdavys';
}

const Pasiulymai: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [invitations, setInvitations] = useState<InterviewInvitationWithEmployer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<number | null>(null);
  const [tab, setTab] = useState<TabKey>('all');

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const { invitations } = await interviewInvitationsApi.myIncoming();
      setInvitations(invitations);
      setError(null);
      window.dispatchEvent(new CustomEvent('interview-invitations-changed'));
    } catch (err: any) {
      setError(err?.message || 'Nepavyko įkelti pasiūlymų');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    refresh();
  }, [isAuthenticated, refresh]);

  const counts = useMemo(() => {
    return {
      all: invitations.length,
      pending: invitations.filter((i) => i.status === 'pending').length,
      accepted: invitations.filter((i) => i.status === 'accepted').length,
      archived: invitations.filter(
        (i) => i.status === 'declined' || i.status === 'withdrawn',
      ).length,
    };
  }, [invitations]);

  const filtered = useMemo(() => {
    switch (tab) {
      case 'pending':
        return invitations.filter((i) => i.status === 'pending');
      case 'accepted':
        return invitations.filter((i) => i.status === 'accepted');
      case 'archived':
        return invitations.filter(
          (i) => i.status === 'declined' || i.status === 'withdrawn',
        );
      default:
        return invitations;
    }
  }, [invitations, tab]);

  if (isLoading) return <div className="invitations-page-loading">Kraunama...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const handleAccept = async (id: number) => {
    setActingId(id);
    try {
      await interviewInvitationsApi.accept(id);
      showToast('Kvietimas patvirtintas — galite pradėti pokalbį', 'success');
      navigate(`/pasiulymai/${id}`);
    } catch (err: any) {
      showToast(err?.message || 'Nepavyko patvirtinti kvietimo', 'error');
      setActingId(null);
    }
  };

  const handleDecline = async (id: number) => {
    setActingId(id);
    try {
      await interviewInvitationsApi.decline(id);
      showToast('Kvietimas atmestas', 'success');
      await refresh();
    } catch (err: any) {
      showToast(err?.message || 'Nepavyko atmesti kvietimo', 'error');
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="invitations-page">
      <div className="invitations-page-header">
        <div className="invitations-page-title">
          <Mail size={22} />
          <h1>Pasiūlymai</h1>
        </div>
      </div>
      <p className="invitations-page-subtitle">
        Darbdavių kvietimai pokalbiui, atsiųsti tiesiai per jūsų CV.
      </p>

      {error && (
        <div className="invitations-alert invitations-alert--error">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {invitations.length === 0 && !loading ? (
        <EmptyState />
      ) : (
        <>
          <div className="invitations-tabs">
            <Tab
              label="Visi"
              count={counts.all}
              active={tab === 'all'}
              onClick={() => setTab('all')}
            />
            <Tab
              label="Laukia atsakymo"
              count={counts.pending}
              active={tab === 'pending'}
              onClick={() => setTab('pending')}
            />
            <Tab
              label="Aktyvūs"
              count={counts.accepted}
              active={tab === 'accepted'}
              onClick={() => setTab('accepted')}
            />
            <Tab
              label="Archyvas"
              count={counts.archived}
              active={tab === 'archived'}
              onClick={() => setTab('archived')}
            />
          </div>

          {loading ? (
            <div className="invitations-page-loading">Kraunama...</div>
          ) : filtered.length === 0 ? (
            <p className="invitations-empty-hint">Šiuo statusu pasiūlymų nėra.</p>
          ) : (
            <div className="invitations-grid">
              {filtered.map((inv) => (
                <CandidateInvitationCard
                  key={inv.id}
                  invitation={inv}
                  acting={actingId === inv.id}
                  onAccept={() => handleAccept(inv.id)}
                  onDecline={() => handleDecline(inv.id)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Tab: React.FC<{
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}> = ({ label, count, active, onClick }) => (
  <button
    type="button"
    className={`invitations-tab ${active ? 'is-active' : ''}`}
    onClick={onClick}
  >
    {label} <span className="invitations-tab-count">{count}</span>
  </button>
);

const CandidateInvitationCard: React.FC<{
  invitation: InterviewInvitationWithEmployer;
  acting: boolean;
  onAccept: () => void;
  onDecline: () => void;
}> = ({ invitation, acting, onAccept, onDecline }) => {
  const isAccepted = invitation.status === 'accepted';
  const isPending = invitation.status === 'pending';
  const isClosed =
    invitation.status === 'declined' || invitation.status === 'withdrawn';

  const cardClass = [
    'invitation-card',
    isAccepted ? 'invitation-card--clickable' : '',
    isClosed ? 'invitation-card--closed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const inner = (
    <>
      <div className="invitation-card-top">
        <span className={`invitation-status invitation-status--${invitation.status}`}>
          {STATUS_LABEL[invitation.status]}
        </span>
        {invitation.unread_count > 0 && (
          <span
            className="invitation-unread-pill"
            title={`${invitation.unread_count} neperskaitytos žinutės`}
          >
            <MessageSquare size={11} />
            {invitation.unread_count}
          </span>
        )}
      </div>

      <h3 className="invitation-name">{employerDisplay(invitation)}</h3>
      <span className="invitation-meta">
        <Clock size={11} />
        {isPending
          ? `Gauta ${formatDateTime(invitation.created_at)}`
          : `Atnaujinta ${formatDateTime(invitation.updated_at)}`}
      </span>

      {invitation.intro_message ? (
        <p className="invitation-intro">{invitation.intro_message}</p>
      ) : (
        <p className="invitation-intro invitation-intro--empty">
          Darbdavys nepaliko žinutės.
        </p>
      )}

      {isPending && (
        <div className="invitation-actions">
          <button
            type="button"
            className="invitation-btn invitation-btn--secondary"
            onClick={onDecline}
            disabled={acting}
          >
            <XIcon size={14} /> Atmesti
          </button>
          <button
            type="button"
            className="invitation-btn invitation-btn--primary"
            onClick={onAccept}
            disabled={acting}
          >
            <Check size={14} /> Patvirtinti
          </button>
        </div>
      )}

      {isAccepted && (
        <span className="invitation-card-cta">
          <MessageSquare size={14} /> Atidaryti pokalbį
        </span>
      )}
    </>
  );

  return isAccepted ? (
    <Link to={`/pasiulymai/${invitation.id}`} className={cardClass}>
      {inner}
    </Link>
  ) : (
    <article className={cardClass}>{inner}</article>
  );
};

const EmptyState: React.FC = () => (
  <div className="invitations-empty-state">
    <div className="invitations-empty-icon">
      <Mail size={42} />
    </div>
    <h2>Kol kas nėra pasiūlymų</h2>
    <p>
      Kai darbdavys atras jūsų CV ir norės pasikalbėti, jo kvietimas atsiras čia.
      Norite gauti daugiau pasiūlymų? Pažymėkite savo CV kaip „Priimu Freelance
      Gig pasiūlymus".
    </p>
  </div>
);

export default Pasiulymai;
